import React, {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal } from 'react-bootstrap';
import './styles.scss';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import {
  SlideShowChangeIntervals,
  SlideShowSpeeds,
  TraversalDirection,
} from './constants';
import useSlideShowPanels, { useSlideShowPanelsProps } from './hooks';
import { IStyle } from 'Styles';
import { Props as ITabPanelProps } from 'components/tab/headless/tabPanel';
import { GetUnifiedExperimentCanvasHeight, showAlertSnackBar } from 'utils/Global';
import WellProcessor, { Props as WellProcessorProps } from '../WellProcessor';
import { useWellList } from '../hooks';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import {
  useAllPlates,
  useAreAllAvailableImagesLoaded,
  useLastSelectedWellNavigationDetails,
} from 'redux/hooks/experiment';
import { getWellNavigationDetails } from './utils';
import Footer from './footer';
import { PlateWellDTO } from 'business/models/Models';
import { setWellNavigationDetails } from 'redux/reducers/experimentDataReducer';
import {
  DropUrlParameters,
  WellNavigationDetails,
} from 'components/Experiment/constants';
import { useSystemProperty } from 'CustomHooks/useSystemProperty';
import { PropertyConstants } from 'enums/PropertyConstants';
import { useNavigate } from 'react-router-dom';
interface Props extends Omit<WellProcessorProps, 'wells'> {
  showModal: boolean;
  setShowModal: Dispatch<React.SetStateAction<boolean>>;
  isSlideShowInitiatedFromCanvas: boolean;
  imagedWells: PlateWellDTO[];
}
let sliderTimeOut = null;
const SlideShow = (props: Props) => {
  const {
    showModal,
    setShowModal,
    displayMode,
    gridLayout,
    isSlideShowInitiatedFromCanvas,
    imagedWells,
    wellKeyGenerator,
    isFocus,
    setWellRef,
  } = props;
  const [slideShowDirection, setSlideShowDirection] = useState<TraversalDirection>(
    TraversalDirection.Row,
  );
  const slideShowChangeIntervalValue = useSystemProperty(
    PropertyConstants.SlideShowChangeInterval,
    SlideShowChangeIntervals.Medium,
  );
  const slideShowChangeIntervalSpeed =
    SlideShowChangeIntervals[slideShowChangeIntervalValue] ??
    SlideShowChangeIntervals.Medium;
  const [slideShowSpeed, setSlideShowSpeed] = useState<number[]>([
    slideShowChangeIntervalSpeed,
  ]);
  const [selectedSidePanelTab, setSelectedSidePanelTab] = useState(null);
  const [pauseSlideShow, setPauseSlideShow] = useState<boolean>(false);
  const SlideShowPanelsProps: useSlideShowPanelsProps = {
    selectedTab: selectedSidePanelTab,
    setSelectedTab: setSelectedSidePanelTab,
    isButtonsDisabled: false,
  };
  const dispatch = useRMDispatch();
  const allPlates = useAllPlates();
  const [, slideShowPanelsContent] = useSlideShowPanels(SlideShowPanelsProps);
  const expId = useRMSelector((state) => state.experiment.expData?.experimentId);
  const navigate = useNavigate();

  const sidePanelWidth = slideShowPanelsContent
    ? (slideShowPanelsContent as React.Component<ITabPanelProps>).props.width
    : 0;

  const _calculateCanvasWidth = () => {
    const widthMainDiv = 100 - sidePanelWidth;
    return (window.innerWidth - window.innerWidth * 0.005) * (widthMainDiv / 100) - 20;
  };
  const sizeDetails = useMemo(
    () => ({
      canvasWidth: _calculateCanvasWidth(),
      canvasHeight: GetUnifiedExperimentCanvasHeight() - 24,
    }),
    [sidePanelWidth, window.innerWidth, window.innerHeight],
  );

  const handleClose = () => {
    if (document.fullscreenElement) {
      document?.exitFullscreen();
    }
    setShowModal(false);
    stopSlideShow();
    if (isSlideShowInitiatedFromCanvas) {
      navigate(`/exp/${expId}`);
    }
  };
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const wellsToShow = useWellList(
    wellNavigationDetails,
    displayMode,
    gridLayout,
    sizeDetails,
  );

  const isAllImagesLoaded = useAreAllAvailableImagesLoaded(
    wellsToShow.map((item) => item.wellDetails),
  );
  const isAllImagesLoadedRef = useRef(isAllImagesLoaded);
  const imagedWellsRef = useRef(imagedWells);
  const slideShowSpeedRef = useRef<number[]>(slideShowSpeed);

  useEffect(() => {
    isAllImagesLoadedRef.current = isAllImagesLoaded;
  }, [isAllImagesLoaded]);

  useEffect(() => {
    imagedWellsRef.current = imagedWells;
  }, [imagedWells]);

  useEffect(() => {
    updateSlideShowSpeed([slideShowChangeIntervalSpeed]);
  }, [slideShowChangeIntervalSpeed]);

  const startSlideShow = (traversalDirection: TraversalDirection = null) => {
    processSlideShow(wellNavigationDetails, traversalDirection);
  };
  const processSlideShow = (
    wellNavigationDetails: WellNavigationDetails,
    traversalDirection: TraversalDirection = null,
  ) => {
    const _traversalDirection =
      traversalDirection !== null ? traversalDirection : slideShowDirection;
    let isLastWell = false;
    const imagedWells = imagedWellsRef?.current;
    const plateNumber =
      allPlates.findIndex((item) => item.plateId === wellNavigationDetails.plateID) + 1;
    let currentWellDetails: WellNavigationDetails = {
      wellNum: wellNavigationDetails.wellNum,
      plateID: wellNavigationDetails.plateID,
      plateNum: plateNumber,
    };
    const totalWells = allPlates[0].cols * allPlates[0].rows;
    if (wellNavigationDetails && isAllImagesLoadedRef?.current) {
      if (
        (currentWellDetails.wellNum === totalWells &&
          currentWellDetails.plateNum === allPlates.length) ||
        (currentWellDetails.plateID === imagedWells[imagedWells.length - 1]?.plateId &&
          currentWellDetails.wellNum === imagedWells[imagedWells.length - 1]?.wellNumber)
      ) {
        isLastWell = true;
        stopSlideShow();
        setPauseSlideShow(true);
        showAlertSnackBar(
          EAlertType.INFO,
          ESnackBarPosition.TOP,
          "you've reached the end of the current experiment",
        );
      }
      currentWellDetails = getWellNavigationDetails(
        allPlates,
        wellNavigationDetails,
        1,
        _traversalDirection,
        imagedWells,
      );
      const parts = window.location.pathname.split('/');
      parts[DropUrlParameters.PLATEID] = currentWellDetails.plateID.toString();
      parts[DropUrlParameters.WELLNUM] = currentWellDetails.wellNum.toString();
      navigate(parts.join('/'), { replace: true });
      dispatch(setWellNavigationDetails([currentWellDetails]));
    }
    if (!isLastWell) {
      sliderTimeOut = setTimeout(() => {
        processSlideShow(currentWellDetails, _traversalDirection);
      }, SlideShowSpeeds[4 - slideShowSpeedRef.current[0]]);
    }
  };
  const stopSlideShow = () => {
    clearTimeout(sliderTimeOut);
    sliderTimeOut = null;
  };

  const updateSlideShowSpeed = useCallback((values: number[]) => {
    slideShowSpeedRef.current = values;
    setSlideShowSpeed(values);
  }, []);

  useEffect(() => {
    if (showModal && imagedWells.length > 0 && sliderTimeOut === null) {
      setPauseSlideShow(false);
      setTimeout(() => {
        startSlideShow();
      }, 500);
    }
    return () => {
      stopSlideShow();
    };
  }, [showModal, imagedWells.length]);

  return (
    <Modal
      id="slideshow-modal-wrapper"
      dialogClassName="slide-show-modal"
      show={showModal}
      onHide={handleClose}
      keyboard={false}
      animation={true}
      enforceFocus={false}
    >
      <Modal.Header id="slideshow-modal-header">
        <div
          id="slideshow-modal-header-title"
          className="font-roboto text-lg font-medium tracking-normal text-left"
        >
          Image Slide Show
        </div>
        <RMCustomButton
          iconProps={{
            id: 'slideshow-modal-header-closebutton',
            name: 'Close',
            height: 18,
            width: 18,
            fill: '#474747',
          }}
          onClick={handleClose}
        />
      </Modal.Header>
      <Modal.Body id="slideshow-modal-body">
        <div
          id="slideshow-modal-body-maincontainer"
          className="flex w-full h-full min-h-full min-w-full bg-[#313131]"
        >
          <div id="slideshow-wellprocessor-container">
            <WellProcessor
              displayMode={displayMode}
              gridLayout={gridLayout}
              wells={wellsToShow}
              isTraversingVertical={slideShowDirection === TraversalDirection.Column}
              imagedWells={imagedWells}
              wellKeyGenerator={wellKeyGenerator}
              isFocus={isFocus}
              setWellRef={setWellRef}
            />
          </div>
          {slideShowPanelsContent && (
            <div
              id="slideshow-sidepanel-container"
              //@ts-ignore
              style={styles.sidePanelRoot(sidePanelWidth)}
            >
              {slideShowPanelsContent}
            </div>
          )}
        </div>
      </Modal.Body>
      <Footer
        slideShowDirection={slideShowDirection}
        setSlideShowDirection={setSlideShowDirection}
        pauseSlideShow={pauseSlideShow}
        setPauseSlideShow={setPauseSlideShow}
        slideShowSpeed={slideShowSpeed}
        setSlideShowSpeed={updateSlideShowSpeed}
        startSlideShow={startSlideShow}
        stopSlideShow={stopSlideShow}
        selectedSidePanelTab={selectedSidePanelTab}
        setSelectedSidePanelTab={setSelectedSidePanelTab}
        imagedWells={imagedWells}
      />
    </Modal>
  );
};

const styles: IStyle = {
  //@ts-ignore
  sidePanelRoot: (width: number) => ({
    position: 'fixed',
    display: 'block',
    width: `${width - 1}%`,
    height: '86.8%',
    top: '6.7%',
    left: `${101 - width}%`,
    right: 0,
    bottom: 0,
    borderLeft: '1px solid lightGray',
    backgroundColor: 'white',
  }),
};

export default SlideShow;
